(function () {
  if (window.mlCountdownClockInitialized) {
    return;
  }

  window.mlCountdownClockInitialized = true;

  const instances = new Map();
  const SECOND_MS = 1000;
  const ACCESS_STORAGE_KEY = 'mlCountdownClockAccess.v1';
  const ACCESS_HASH = 'ee378c4fcd6f8c927f62c655c9f0ee4fd77b0dc68810d368cd479b2741c26e9d';
  const ACCESS_CLASS = 'ml-countdown-access-granted';
  const UPDATE_CLASS = 'is-updating';

  const pad = (value) => String(value).padStart(2, '0');
  const pluralize = (value, label) => `${value} ${label}${value === 1 ? '' : 's'}`;
  const bufferToHex = (buffer) => Array.from(new Uint8Array(buffer), (value) => value.toString(16).padStart(2, '0')).join('');

  const readAccessToken = () => {
    try {
      return window.localStorage ? window.localStorage.getItem(ACCESS_STORAGE_KEY) : null;
    } catch (error) {
      return null;
    }
  };

  const writeAccessToken = (value) => {
    try {
      if (!window.localStorage) {
        return false;
      }

      if (value) {
        window.localStorage.setItem(ACCESS_STORAGE_KEY, value);
      } else {
        window.localStorage.removeItem(ACCESS_STORAGE_KEY);
      }

      return true;
    } catch (error) {
      return false;
    }
  };

  const isAccessGranted = () => readAccessToken() === ACCESS_HASH || window.mlCountdownClockAccessGranted === true;

  const syncAccessClass = () => {
    const granted = isAccessGranted();
    window.mlCountdownClockAccessGranted = granted;
    document.documentElement.classList.toggle(ACCESS_CLASS, granted);
    return granted;
  };

  const setGateMessage = (instance, text, state) => {
    if (!instance.gate.message) {
      return;
    }

    instance.gate.message.textContent = text;
    instance.gate.message.dataset.state = state || 'default';
  };

  const setAccessState = (instance, unlocked) => {
    instance.root.dataset.accessState = unlocked ? 'unlocked' : 'locked';

    if (instance.gate.stage) {
      instance.gate.stage.setAttribute('aria-hidden', unlocked ? 'false' : 'true');
    }

    if (instance.gate.container) {
      instance.gate.container.setAttribute('aria-hidden', unlocked ? 'true' : 'false');
    }

    if (instance.gate.input) {
      instance.gate.input.disabled = unlocked;

      if (unlocked) {
        instance.gate.input.value = '';
      }
    }

    if (instance.gate.button) {
      instance.gate.button.disabled = unlocked;
      instance.gate.button.removeAttribute('aria-busy');
    }
  };

  const unlockAll = (activeInstance, remembered) => {
    window.mlCountdownClockAccessGranted = true;
    document.documentElement.classList.add(ACCESS_CLASS);

    instances.forEach((instance) => {
      setAccessState(instance, true);

      if (instance === activeInstance) {
        setGateMessage(instance, remembered ? 'Access confirmed. This browser will remember this device.' : 'Access confirmed for this session.', 'success');
      }
    });
  };

  const hashValue = async (value) => {
    if (!window.crypto || !window.crypto.subtle || typeof window.TextEncoder === 'undefined') {
      throw new Error('unavailable');
    }

    const digest = await window.crypto.subtle.digest('SHA-256', new window.TextEncoder().encode(value));
    return bufferToHex(digest);
  };

  const initGate = (instance) => {
    if (!instance.gate.form || !instance.gate.input || !instance.gate.button) {
      setAccessState(instance, true);
      return;
    }

    if (syncAccessClass()) {
      setAccessState(instance, true);
      setGateMessage(instance, 'Access confirmed. This browser will remember this device.', 'success');
      return;
    }

    setAccessState(instance, false);
    setGateMessage(instance, 'Enter the access code to continue.', 'default');

    instance.handleGateSubmit = async (event) => {
      event.preventDefault();

      const submittedValue = instance.gate.input.value.trim();

      if (!submittedValue) {
        setGateMessage(instance, 'Enter the access code to continue.', 'default');
        instance.gate.input.focus();
        return;
      }

      instance.gate.button.disabled = true;
      instance.gate.button.setAttribute('aria-busy', 'true');
      setGateMessage(instance, 'Checking…', 'default');

      try {
        const submittedHash = await hashValue(submittedValue);

        if (submittedHash === ACCESS_HASH) {
          const remembered = writeAccessToken(ACCESS_HASH);
          syncAccessClass();
          unlockAll(instance, remembered);
          return;
        }

        setGateMessage(instance, 'Code not recognized. Try again.', 'error');
        instance.gate.input.select();
      } catch (error) {
        setGateMessage(instance, 'This browser could not verify the access code.', 'error');
      }

      if (instance.root.dataset.accessState !== 'unlocked') {
        instance.gate.button.disabled = false;
        instance.gate.button.removeAttribute('aria-busy');
      }
    };

    instance.gate.form.addEventListener('submit', instance.handleGateSubmit);
  };

  const animateValue = (element) => {
    if (!element) {
      return;
    }

    element.classList.remove(UPDATE_CLASS);

    window.requestAnimationFrame(() => {
      element.classList.add(UPDATE_CLASS);
      window.setTimeout(() => {
        element.classList.remove(UPDATE_CLASS);
      }, 360);
    });
  };

  const updateInstance = (instance) => {
    const now = Date.now();
    const remainingMs = Math.max(instance.targetEpoch - now, 0);
    const remainingSeconds = Math.floor(remainingMs / SECOND_MS);
    const days = Math.floor(remainingSeconds / 86400);
    const hours = Math.floor((remainingSeconds % 86400) / 3600);
    const minutes = Math.floor((remainingSeconds % 3600) / 60);
    const seconds = remainingSeconds % 60;

    instance.root.dataset.countdownComplete = remainingMs === 0 ? 'true' : 'false';

    if (instance.lastSecond !== remainingSeconds) {
      const nextValues = {
        days: pad(days),
        hours: pad(hours),
        minutes: pad(minutes),
        seconds: pad(seconds)
      };

      Object.keys(nextValues).forEach((key) => {
        if (!instance.values[key]) {
          return;
        }

        if (instance.values[key].textContent !== nextValues[key]) {
          instance.values[key].textContent = nextValues[key];
          animateValue(instance.values[key]);
        }
      });

      const ariaLabel = remainingMs > 0
        ? `${pluralize(days, 'day')} ${pluralize(hours, 'hour')} ${pluralize(minutes, 'minute')} ${pluralize(seconds, 'second')} remaining`
        : instance.completeText;

      instance.timer.setAttribute('aria-label', ariaLabel);
      instance.lastSecond = remainingSeconds;
    }

    if (remainingMs > 0) {
      const delay = SECOND_MS - (Date.now() % SECOND_MS) + 12;
      instance.timeoutId = window.setTimeout(() => updateInstance(instance), delay);
    } else {
      instance.timeoutId = null;
    }
  };

  const destroyInstance = (root) => {
    const instance = instances.get(root);

    if (!instance) {
      return;
    }

    if (instance.timeoutId) {
      window.clearTimeout(instance.timeoutId);
    }

    if (instance.handleGateSubmit && instance.gate.form) {
      instance.gate.form.removeEventListener('submit', instance.handleGateSubmit);
    }

    instances.delete(root);
  };

  const initInstance = (root) => {
    if (!root || instances.has(root)) {
      return;
    }

    const values = {
      days: root.querySelector('[data-countdown-value="days"]'),
      hours: root.querySelector('[data-countdown-value="hours"]'),
      minutes: root.querySelector('[data-countdown-value="minutes"]'),
      seconds: root.querySelector('[data-countdown-value="seconds"]')
    };

    const timer = root.querySelector('[data-countdown-timer]');

    if (!values.days || !values.hours || !values.minutes || !values.seconds || !timer) {
      return;
    }

    const instance = {
      root,
      targetEpoch: Number(root.dataset.targetEpoch),
      completeText: root.dataset.completeText || 'Reached',
      values,
      timer,
      gate: {
        stage: root.querySelector('[data-countdown-stage]'),
        container: root.querySelector('[data-countdown-gate]'),
        form: root.querySelector('[data-countdown-gate-form]'),
        input: root.querySelector('[data-countdown-gate-input]'),
        button: root.querySelector('[data-countdown-gate-button]'),
        message: root.querySelector('[data-countdown-gate-message]')
      },
      lastSecond: null,
      timeoutId: null,
      handleGateSubmit: null
    };

    instances.set(root, instance);
    initGate(instance);
    updateInstance(instance);
  };

  const initAll = (scope) => {
    const root = scope && scope.querySelectorAll ? scope : document;
    root.querySelectorAll('[data-countdown-root]').forEach(initInstance);
  };

  const destroyAllInScope = (scope) => {
    const root = scope && scope.querySelectorAll ? scope : document;
    root.querySelectorAll('[data-countdown-root]').forEach(destroyInstance);
  };

  syncAccessClass();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroyAllInScope(event.target);
  });
})();
